import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import LanguageLink from "../routing/LanguageLink";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import api from "../../utils/api";

const UserRegistrationForm = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
  } = useForm();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const onSubmit = async (data) => {
    try {
      let deviceId = localStorage.getItem("device_id");
      if (!deviceId) {
        deviceId = `guest_${Math.random().toString(36).substr(2, 12)}_${Date.now()}`;
        localStorage.setItem("device_id", deviceId);
      }

      await api.post("/auth/register", data, {
        headers: {
          "X-Device-ID": deviceId,
        },
      });

      Swal.fire({
        icon: "success",
        title: "Registration Successful!",
        text: "Please check your email to verify.",
        showConfirmButton: true,
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        allowOutsideClick: false,
      }).then(() => {
        navigate(`/${i18n.language}/verify-otp`, {
          state: { email: data.email },
        });
      });
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Registration failed. Please try again.";

      Swal.fire({
        icon: "error",
        title: "Error",
        text: message,
        showConfirmButton: true,
        confirmButtonText: "OK",
        confirmButtonColor: "#dc2626",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Full Name *
        </label>
        <input
          {...register("name", {
            required: "Full name is required",
            pattern: {
              value: /^[A-Za-z\s]+$/,
              message: "Name must contain letters only",
            }
          })}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-all ${errors.name ? "border-red-300" : "border-gray-300"
            }`}
          placeholder="Enter your full name"
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Email Address *
        </label>
        <input
          {...register("email", {
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email format",
            },
          })}
          type="email"
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-all ${errors.email ? "border-red-300" : "border-gray-300"
            }`}
          placeholder="your@email.com"
        />
        {errors.email && (
          <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Password *
        </label>
        <input
          {...register("password", {
            required: "Password is required",
            minLength: {
              value: 8,
              message: "Password must be at least 8 characters",
            },
          })}
          type="password"
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-all ${errors.password ? "border-red-300" : "border-gray-300"
            }`}
          placeholder="Create a strong password"
        />
        {errors.password && (
          <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Confirm Password *
        </label>
        <input
          {...register("password_confirmation", {
            required: "Please confirm your password",
            validate: (value) =>
              value === watch("password") || "Passwords do not match",
          })}
          type="password"
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-all ${errors.password_confirmation ? "border-red-300" : "border-gray-300"
            }`}
          placeholder="Re-enter your password"
        />
        {errors.password_confirmation && (
          <p className="mt-1 text-sm text-red-600">
            {errors.password_confirmation.message}
          </p>
        )}
      </div>

      <div className="flex items-start space-x-3">
        <input
          {...register("termsAccepted", {
            required: "You must accept the terms",
          })}
          type="checkbox"
          className="mt-1 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
        />
        <label className="text-sm text-gray-600">
          I agree to the{" "}
          <LanguageLink to="/terms-of-service" className="text-blue-600 hover:underline">
            Terms of Service
          </LanguageLink>{" "}
          and{" "}
          <LanguageLink to="/privacy-policy" className="text-blue-600 hover:underline">
            Privacy Policy
          </LanguageLink>
        </label>
      </div>
      {errors.termsAccepted && (
        <p className="text-sm text-red-600">{errors.termsAccepted.message}</p>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-all"
      >
        {isSubmitting ? "Creating Account..." : "Create Account"}
      </button>
    </form>
  );
};

export default UserRegistrationForm;